import { type TFunction } from 'i18next';
import { isEqual } from 'lodash';

import { AtlasGqlInspectionDueWindow } from 'types/atlas-graphql';
import { TABLE_FILTER_OPERATORS } from 'utils/constants';

/** Column key of the Next Inspection Due column; also the Asset `nextInspectionDue` filter key. */
export const NEXT_INSPECTION_DUE_KEY = 'nextInspectionDue';

export type NextInspectionDueOptionId = 'pastDue' | 'dueWithin1Month' | 'dueWithin3Months';

/**
 * Values are relative tokens, so saved views and links stay relative to the current day.
 * - Past Due: `nextInspectionDue < today`; also matches Assets due now (a rule never Inspected),
 *   which the server treats as due before every date
 * - Due within N months: `today <= nextInspectionDue <= today + N months`
 * Assets without a schedule never match. `window` is the same window in the Site summary API, so
 * its counts match the table.
 */
const NEXT_INSPECTION_DUE_OPTION_BY_ID: Record<
  NextInspectionDueOptionId,
  { value: string[]; window: AtlasGqlInspectionDueWindow; getLabel: (t: TFunction) => string }
> = {
  pastDue: {
    value: [TABLE_FILTER_OPERATORS.LESS_THAN, 'rel:0D'],
    window: AtlasGqlInspectionDueWindow.PastDue,
    getLabel: t => t('assets.nextInspectionDue.filter.pastDue', 'Past Due'),
  },
  dueWithin1Month: {
    value: ['rel:0D', 'rel:1M'],
    window: AtlasGqlInspectionDueWindow.Due_1Month,
    getLabel: t => t('assets.nextInspectionDue.filter.dueWithin1Month', 'Due within 1 Month'),
  },
  dueWithin3Months: {
    value: ['rel:0D', 'rel:3M'],
    window: AtlasGqlInspectionDueWindow.Due_3Months,
    getLabel: t => t('assets.nextInspectionDue.filter.dueWithin3Months', 'Due within 3 Months'),
  },
};

/** The options in display order. */
export const NEXT_INSPECTION_DUE_OPTIONS = (
  ['pastDue', 'dueWithin1Month', 'dueWithin3Months'] as const
).map(id => ({ id, ...NEXT_INSPECTION_DUE_OPTION_BY_ID[id] }));

export const getNextInspectionDueOption = (id: NextInspectionDueOptionId) =>
  NEXT_INSPECTION_DUE_OPTION_BY_ID[id];

export const getNextInspectionDueOptionValue = (id: NextInspectionDueOptionId) =>
  getNextInspectionDueOption(id).value;

export const getSelectedNextInspectionDueOption = (
  selectedKeys: unknown
): NextInspectionDueOptionId | undefined =>
  NEXT_INSPECTION_DUE_OPTIONS.find(({ value }) => isEqual(value, selectedKeys))?.id;

/**
 * The column's `parseUrlFilter`: links use the option ids (`filter.nextInspectionDue=pastDue`).
 * The filter takes a single option, so the first known id wins; unknown ids are dropped.
 */
export const parseNextInspectionDueUrlFilter = (values: string[]) =>
  values
    .map(value => NEXT_INSPECTION_DUE_OPTIONS.find(({ id }) => id === value)?.value)
    .find(Boolean);
